import React, { useEffect, useState } from "react";
import KPICard from "@/components/KPICard";
import RegionPlainCard from "@/components/RegionPlainCard";
import RegionGroupedList from "@/components/RegionGroupedList";
import StressLeaderboard from "@/components/StressLeaderboard";
import GlossaryPanel from "@/components/GlossaryPanel";
import WetterBodenLayer from "@/components/dashboard/WetterBodenLayer";
import InfoHint from "@/components/InfoHint";
import { fetchAllSnapshots, triggerPipeline } from "@/lib/api";
import { regionHealth, healthBand, toneColor } from "@/lib/laymen";
import { toast } from "sonner";
import { ArrowsClockwise, Newspaper, Printer, Compass } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { StoryCard, ExplainPanel } from "@/components/Explain";
import GlossaryTerm from "@/components/GlossaryTerm";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const d = await fetchAllSnapshots();
      setData(d);
    } catch (e) {
      toast.error("Fehler beim Laden der Snapshots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    toast.message("Pipeline wird ausgeführt…");
    try {
      await triggerPipeline();
      await load();
      toast.success("Pipeline abgeschlossen");
    } catch {
      toast.error("Pipeline fehlgeschlagen");
    } finally {
      setRefreshing(false);
    }
  };

  const snaps = data?.snapshots || [];
  const regions = data?.regions || [];
  const primary = snaps[0];

  // Fleet-wide ecosystem health: average of per-region scores
  const fleetScore = snaps.length
    ? Math.round(snaps.reduce((a, s) => a + regionHealth(s.metrics), 0) / snaps.length)
    : 0;
  const fleetBand = healthBand(fleetScore);
  const fleetTone = toneColor(fleetBand.level);

  const avgNdvi = snaps.length
    ? (snaps.reduce((a, s) => a + (s.metrics?.ndvi_current || 0), 0) / snaps.length).toFixed(3)
    : "–";
  const priceDelta = primary?.metrics?.price_change_7d_pct;
  const yieldIdx = primary?.metrics?.yield_forecast_index;
  // Choose a forest-flavoured region for the forest KPI.
  const forestSnap = snaps.find(
    (s) => s.region_id?.includes("gmunden") || s.region_id?.includes("salzburg") || s.region_id?.includes("tirol")
  ) || snaps.find((s) => (s.metrics?.forest_vitality_index ?? 0) > 0);
  const forestIdx = forestSnap?.metrics?.forest_vitality_index;
  const forestLabel = regions.find((r) => r.id === forestSnap?.region_id)?.label || "Region";

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Overview · Europe Central</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tighter mt-1 leading-[0.95]">Daten-Cockpit</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
            Eine einzige Übersicht aus drei Datenquellen: Satellit (Copernicus Sentinel),
            Wetter (Open-Meteo) und Märkte (Agrar-Futures). Alle Aufrufe laufen über
            Bodenblick-Proxies — bereinigt, anonymisiert, ohne Tracking.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/insights"
            data-testid="insights-link"
            className="flex items-center gap-2 border border-white/10 text-white bg-[#121212] hover:bg-white/5 transition-colors rounded-sm px-4 py-2 text-sm"
          >
            <Newspaper size={16} /> Weekly Insights
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="print-dashboard-btn"
            className="flex items-center gap-2 border border-white/10 text-white bg-[#121212] hover:bg-white/5 transition-colors rounded-sm px-4 py-2 text-sm print-hide"
          >
            <Printer size={16} /> PDF / Drucken
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-pipeline-btn"
            className="flex items-center gap-2 bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 transition-colors rounded-sm px-4 py-2 text-sm font-medium print-hide"
          >
            <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Pipeline läuft…" : "Pipeline ausführen"}
          </button>
        </div>
      </header>

      {loading ? (
        <div className="eid-card p-8 text-sm text-zinc-400" data-testid="dashboard-loading">
          Daten werden geladen…
        </div>
      ) : (
        <>
          <StoryCard
            eyebrow="So liest du das Cockpit"
            title="Vier Kennzahlen erzählen die Lage in drei Sekunden."
            lead="Oben links der Gesamtzustand all deiner Regionen, daneben die wichtigsten Detail-Kennzahlen. Wenn dich eine Zahl irritiert, klick auf das Info-Symbol — wir erklären jedes Konzept in einem Satz."
            bullets={[
              "Gesamt-Score 0–100: Mittelwert aller Regionen — eine einzige Zahl, eine Wahrheit",
              "NDVI = wie grün und chlorophyll-reich deine Flächen gerade sind",
              "Ertrags-Index = unsere Vorhersage für die kommende Ernte, kalibriert an 5 Jahren Historie",
            ]}
            accent="#00FA9A"
            icon={Compass}
            testid="dashboard-story"
          />
          <ExplainPanel
            title="Was passiert hier eigentlich?"
            accent="#00FA9A"
            testid="dashboard-explain"
          >
            <p>
              Das Cockpit kombiniert <strong>drei Live-Datenströme</strong> zu einer einzigen Übersicht:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><GlossaryTerm word="Copernicus">Copernicus</GlossaryTerm> / <GlossaryTerm word="Sentinel-2">Sentinel-2</GlossaryTerm> liefert alle 5 Tage Satellitenbilder mit ~10 m Auflösung. Daraus berechnen wir <GlossaryTerm word="NDVI">NDVI</GlossaryTerm>, <GlossaryTerm word="NDWI">NDWI</GlossaryTerm> und andere Vegetations-Indizes.</li>
              <li><GlossaryTerm word="Open-Meteo">Open-Meteo</GlossaryTerm> (<GlossaryTerm word="ERA5">ERA5</GlossaryTerm> + DWD-ICON) liefert Wetterhistorie, Bodenfeuchte und Vorhersagen.</li>
              <li><strong>Agrar-Futures-Börsen</strong> für Weizen, Mais und Raps — geben uns den Preis-Kontext.</li>
            </ul>
            <p>
              Aus diesen Roh-Daten berechnen wir einen <strong>Gesundheits-Score 0–100</strong> pro Region, eine Ertrags-Prognose und konkrete Handlungs-Empfehlungen. Alle Berechnungen laufen anonymisiert auf unseren Servern — kein Drittanbieter sieht deine Felder.
            </p>
            <div className="grid sm:grid-cols-2 gap-2 mt-1">
              <Link to="/quick" className="border border-[#00FA9A]/40 p-3 rounded-sm hover:bg-[#00FA9A]/20 transition-colors">
                <div className="text-xs font-mono uppercase tracking-wider text-[#00FA9A]">unterwegs?</div>
                <div className="text-sm text-white mt-0.5">→ Tages-Ampel (mobil-optimiert)</div>
              </Link>
              <Link to="/feldfruechte" className="border border-[#00FA9A]/40 p-3 rounded-sm hover:bg-[#00FA9A]/20 transition-colors">
                <div className="text-xs font-mono uppercase tracking-wider text-[#00FA9A]">neu</div>
                <div className="text-sm text-white mt-0.5">→ Wie erkennt Bodenblick Feldfrüchte?</div>
              </Link>
            </div>
          </ExplainPanel>

          <section
            className="eid-card p-5 md:p-6 flex flex-col lg:flex-row items-start lg:items-center gap-6"
            data-testid="fleet-health-hero"
            style={{ borderLeft: `3px solid ${fleetTone.dot}` }}
          >
            <div className="flex items-center gap-5">
              <FleetRing score={fleetScore} color={fleetTone.dot} />
              <div>
                <div className="eid-label flex items-center gap-2">
                  <span>Gesamt-Ökosystemzustand</span>
                  <InfoHint
                    testid="info-fleet-score"
                    title="Gesamt-Ökosystemzustand"
                    text="Ein zusammengefasster Gesundheitsscore (0–100) über alle deine Regionen. Reinrechnen: NDVI, Bodenfeuchte-Defizit, Ertrags-Index und Forst-Vitalität. Über 75 = alles läuft. Unter 40 = mindestens eine Fläche braucht Aufmerksamkeit."
                  />
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="text-4xl font-mono tracking-tight">{fleetScore}</span>
                  <span className="text-sm font-medium" style={{ color: fleetTone.fg }}>
                    {fleetBand.label}
                  </span>
                </div>
                <div className="text-sm text-zinc-400 mt-1">
                  Aggregierte Gesundheit aller {snaps.length} beobachteten Regionen.
                </div>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-3 min-w-[280px] lg:ml-auto">
              <HeroStat label="Regionen" value={snaps.length} />
              <HeroStat label="Ø NDVI" value={avgNdvi} />
              <HeroStat
                label="Datenquellen"
                value={snaps[0]?.satellite?.source === "copernicus" ? "live" : "mock"}
                tone={snaps[0]?.satellite?.source === "copernicus" ? "good" : "warn"}
              />
            </div>
          </section>

          <section data-testid="kpi-section" className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-medium">Schlüssel-Kennzahlen</h2>
              <InfoHint
                testid="info-kpi"
                title="Schlüssel-Kennzahlen (KPIs)"
                text="Vier Kacheln mit aktuellen Werten, Delta-Badges (Veränderung) und Trend-Sparkline der letzten 30 Tage. Die Pfeilrichtung der Sparkline ist wichtiger als der Zahlenwert — fallende Linien sind Frühwarnungen."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="kpi-grid">
            <KPICard
              label="Ø NDVI · alle Regionen"
              value={avgNdvi}
              unit="Index"
              delta={primary?.metrics?.ndvi_anomaly}
              deltaLabel=" Δ"
              series={primary?.satellite?.ndvi}
              color="#00FA9A"
              testid="kpi-ndvi"
            />
            <KPICard
              label="Bodenfeuchte-Defizit"
              value={(snaps.reduce((a, s) => a + (s.metrics?.soil_moisture_deficit || 0), 0) / Math.max(1, snaps.length)).toFixed(3)}
              unit="m³/m³"
              delta={primary?.metrics?.soil_moisture_deficit}
              deltaLabel=""
              series={primary?.weather?.soil_moisture_0_7cm}
              color="#4DA3FF"
              testid="kpi-soil"
            />
            <KPICard
              label="Ertragsprognose-Index"
              value={yieldIdx?.toFixed?.(1) ?? "–"}
              unit="/100"
              delta={priceDelta}
              deltaLabel="%"
              series={primary?.financial?.close}
              color="#FFC24B"
              testid="kpi-yield"
            />
            <KPICard
              label={`Forst-Vitalität · ${forestLabel}`}
              value={forestIdx?.toFixed?.(1) ?? "–"}
              unit="/100"
              delta={forestSnap?.metrics?.ndvi_anomaly}
              deltaLabel=" Δ NDVI"
              series={forestSnap?.satellite?.ndmi}
              color="#FF5500"
              testid="kpi-forest"
            />
            </div>
          </section>

          <WetterBodenLayer snaps={snaps} />

          <GlossaryPanel />

          {regions.length >= 5 && (
            <StressLeaderboard regions={regions} snapshots={snaps} limit={5} />
          )}

          <section className="space-y-3" data-testid="plain-cards">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-medium flex items-center gap-2">
                Klartext pro Region
                <InfoHint
                  testid="info-plain-cards"
                  title="Klartext pro Region"
                  text="Ausführliche Karten pro Fläche. Zeigen Gesundheitsring, Status-Blöcke (NDVI, Bodenwasser, Ernte, Preis) mit Ampel-Farben und automatische Handlungsempfehlungen. Rote oder orange Tipps IMMER zuerst lesen."
                  placement="left"
                />
              </h2>
              <div className="eid-label">Für Landwirte &amp; Anwender</div>
            </div>
            {/* Use grouped list when we have many regions, plain cards for first few */}
            {regions.length > 6 ? (
              <RegionGroupedList
                regions={regions}
                snapshots={snaps}
                countries={data?.countries || []}
                testid="regions-grouped"
              />
            ) : (
              regions.map((r) => {
                const snap = snaps.find((s) => s.region_id === r.id);
                return (
                  <RegionPlainCard
                    key={r.id}
                    region={r}
                    snap={snap}
                    testid={`plain-card-${r.id}`}
                  />
                );
              })
            )}
          </section>
        </>
      )}
    </div>
  );
}

function HeroStat({ label, value, tone }) {
  const c = toneColor(tone);
  return (
    <div className="border border-white/10 rounded-sm p-3 bg-[#121212]">
      <div className="eid-label">{label}</div>
      <div className="text-2xl font-mono mt-1" style={{ color: tone ? c.fg : "#F5F5F5" }}>
        {value}
      </div>
    </div>
  );
}

function FleetRing({ score, color }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - score / 100);
  return (
    <svg width="110" height="110" viewBox="0 0 110 110">
      <circle cx="55" cy="55" r={r} fill="none" stroke="#141414" strokeWidth="8" />
      <circle
        cx="55" cy="55" r={r}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 55 55)"
        style={{ transition: "stroke-dashoffset 800ms ease-out" }}
      />
      <text x="55" y="62" textAnchor="middle" fontSize="22" fontFamily="JetBrains Mono" fill="#FFFFFF">
        {score}
      </text>
    </svg>
  );
}
